import { RA_SETTING_KEYS, RA_SETTING_LIMITS, type RaSettingsValues } from '@felix/domain';
import { Alert, Button, NumberInput, Skeleton } from '@mantine/core';
import { IconAlertCircle, IconShieldCheck } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Panel } from '../../Panel.js';
import { errorCode } from '../personnel/source.js';
import type { RaSettings, RaSettingsSource } from '../risk/dashboard/types.js';
import { CardFooter, Fact, Facts, settingsClasses } from './SettingsLayout.js';

/** Ayarın birimi (ay, gün, saat). */
const UNIT: Readonly<Record<keyof RaSettingsValues, 'months' | 'days' | 'hours'>> = {
  reviewMonths: 'months',
  reviewDueDays: 'days',
  authorizationMaxHours: 'hours',
  acknowledgementDueDays: 'days',
};

export interface RaSettingsPanelProps {
  scope: 'web' | 'ship';
  settings: RaSettings | null;
  error?: string | null;
  onSave?: (rowVersion: number | null, values: Partial<RaSettingsValues>) => Promise<void>;
}

/**
 * Settings ▸ Risk Assessment (K7; ADR 0095 §3; README R25): gözden geçirme aralığı ve uyarı
 * penceresi, operasyon yetkisinin en uzun geçerliliği, okundu onayının gecikme eşiği. Web'de
 * "Ayarlar / düzenleme" yetkisiyle düzenlenir; gemide salt okunur. Ayar kuralı yalnız sıkılaştırır:
 * 12 ay ve 24 saat kesin sınırdır.
 */
export function RaSettingsPanel({ scope, settings, error, onSave }: RaSettingsPanelProps) {
  const { t } = useTranslation();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`riskDashboard.settingsCard.${key}`, options);
  const [values, setValues] = useState<Partial<Record<keyof RaSettingsValues, number | ''>>>({});
  const [failure, setFailure] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  // biome-ignore lint/correctness/useExhaustiveDependencies: ayar sürümü değişince kurulur
  useEffect(() => {
    if (!settings) return;
    setValues(Object.fromEntries(RA_SETTING_KEYS.map((key) => [key, settings[key]])));
  }, [settings?.rowVersion, settings?.reviewMonths]);
  const editable = scope === 'web' && Boolean(onSave);
  const changed =
    settings !== null &&
    RA_SETTING_KEYS.some((key) => values[key] !== undefined && values[key] !== settings[key]);
  const invalid = RA_SETTING_KEYS.some((key) => values[key] === '');
  const unit = (key: keyof RaSettingsValues) => k(`units.${UNIT[key]}`);
  return (
    <Panel title={k('title')} icon={<IconShieldCheck size={18} stroke={1.8} />} divider>
      {error ? (
        <Alert color="red" icon={<IconAlertCircle size={16} />} role="alert">
          {t(`riskDashboard.errors.${error}`, { defaultValue: '' }) || k('loadError')}
        </Alert>
      ) : !settings ? (
        <div aria-busy="true">
          <Skeleton height={18} mb={10} />
          <Skeleton height={18} mb={10} />
          <Skeleton height={18} />
        </div>
      ) : (
        <>
          <p className={settingsClasses.intro}>{k('intro')}</p>
          <Facts>
            {RA_SETTING_KEYS.map((key) => {
              const limit = RA_SETTING_LIMITS[key];
              return (
                <Fact key={key} label={k(`fields.${key}`)} help={k(`hints.${key}`)}>
                  {editable ? (
                    <NumberInput
                      className={settingsClasses.select}
                      aria-label={k(`fields.${key}`)}
                      min={limit.min}
                      max={limit.max}
                      allowDecimal={false}
                      clampBehavior="strict"
                      rightSection={<span className={settingsClasses.unit}>{unit(key)}</span>}
                      rightSectionWidth={56}
                      value={values[key] ?? ''}
                      onChange={(value) => {
                        setSaved(false);
                        setValues((current) => ({
                          ...current,
                          [key]: typeof value === 'number' ? value : '',
                        }));
                      }}
                    />
                  ) : (
                    `${settings[key]} ${unit(key)}`
                  )}
                </Fact>
              );
            })}
          </Facts>
          {scope === 'ship' ? <p className={settingsClasses.note}>{k('readOnly')}</p> : null}
          {failure ? (
            <Alert color="red" icon={<IconAlertCircle size={16} />} role="alert" mt="sm">
              {t(`riskDashboard.errors.${failure}`, { defaultValue: '' }) ||
                `${k('saveError')} (${failure})`}
            </Alert>
          ) : saved ? (
            <Alert color="green" role="status" mt="sm">
              {k('saved')}
            </Alert>
          ) : null}
          {editable ? (
            <CardFooter>
              <Button
                loading={busy}
                disabled={!changed || invalid}
                onClick={async () => {
                  if (!onSave) return;
                  setBusy(true);
                  setFailure(null);
                  try {
                    const patch: Partial<RaSettingsValues> = {};
                    for (const key of RA_SETTING_KEYS) {
                      const value = values[key];
                      if (typeof value === 'number' && value !== settings[key]) patch[key] = value;
                    }
                    await onSave(settings.rowVersion, patch);
                    setSaved(true);
                  } catch (caught) {
                    setFailure(errorCode(caught));
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                {k('save')}
              </Button>
            </CardFooter>
          ) : null}
        </>
      )}
    </Panel>
  );
}

/** Bölümün veri bağlantısı: ayarlar ve düzenleme yetkisi kaynaktan. */
export function RaSettingsSection({ source }: { source: RaSettingsSource }) {
  const [state, setState] = useState<{
    settings: RaSettings | null;
    edit: boolean;
    error: string | null;
  }>({ settings: null, edit: false, error: null });
  useEffect(() => {
    let live = true;
    source
      .load()
      .then((value) => {
        if (live) setState({ settings: value.settings, edit: value.can.edit, error: null });
      })
      .catch((caught: unknown) => {
        if (live) setState({ settings: null, edit: false, error: errorCode(caught) });
      });
    return () => {
      live = false;
    };
  }, [source]);
  const save = source.save;
  return (
    <RaSettingsPanel
      scope={source.scope}
      settings={state.settings}
      error={state.error}
      {...(save && state.edit
        ? {
            onSave: async (rowVersion, values) => {
              const next = await save(rowVersion, values);
              setState({ settings: next.settings, edit: next.can.edit, error: null });
            },
          }
        : {})}
    />
  );
}
